import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { json } from "@remix-run/node";
import { Link, useLoaderData, useOutletContext } from "@remix-run/react";
import prisma from "~/utils/db.server";
import type { Locale } from "~/utils/locale";
import { localePath } from "~/utils/locale";
import { resolveMarket } from "~/services/markets/marketDetection.server";
import { useMarketPrefix } from "~/utils/useMarketPrefix";

/**
 * Wave-9 website editor: partner-authored pages, live at
 * /{market}/pages/{slug} once published. Draft pages 404 for customers;
 * the console's ?in_preview flow can still show them to the partner.
 */
export const loader = async ({ request, params }: LoaderFunctionArgs) => {
  const shop = await prisma.shop.findFirst({ select: { id: true } });
  if (!shop) throw new Response("Not Found", { status: 404 });
  const { market } = await resolveMarket(request, shop.id).catch(() => ({ market: null as null }));

  const inPreview = new URL(request.url).searchParams.get("in_preview") === "1";
  const slug = String(params.slug || "");
  let page = market?.marketId
    ? await prisma.marketPage.findFirst({
        where: {
          marketId: market.marketId,
          slug,
          ...(inPreview ? {} : { published: true }),
        },
        select: {
          title: true,
          bodyText: true,
          images: true,
          ctaLabel: true,
          ctaLink: true,
          seoTitle: true,
          seoDescription: true,
        },
      })
    : null;
  // Shop-level CMS fallback (2026-08-27): pages authored in Admin →
  // Content → Pages render here for the main domain too — and are the
  // vehicle for content-page overrides (contentOverride.server.ts).
  if (!page) {
    const shopPage = await prisma.contentPage.findFirst({
      where: { shopId: shop.id, slug, status: "PUBLISHED" },
      select: { title: true, body: true, seoTitle: true, seoDescription: true },
    });
    if (shopPage) {
      page = {
        title: shopPage.title,
        bodyText: shopPage.body,
        images: null,
        ctaLabel: null,
        ctaLink: null,
        seoTitle: shopPage.seoTitle,
        seoDescription: shopPage.seoDescription,
      };
    }
  }
  if (!page) throw new Response("Not Found", { status: 404 });

  return json({
    page: { ...page, images: (page.images as string[] | null) ?? [] },
  });
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data?.page) return [{ title: "Page not found" }];
  return [
    { title: data.page.seoTitle || data.page.title },
    ...(data.page.seoDescription ? [{ name: "description", content: data.page.seoDescription }] : []),
  ];
};

export default function MarketCustomPage() {
  const { page } = useLoaderData<typeof loader>();
  const { locale } = useOutletContext<{ locale: Locale }>();
  const marketPrefix = useMarketPrefix();
  const paragraphs = (page.bodyText || "")
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);

  return (
    <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 lg:py-16">
      <h1 className="font-display text-3xl lg:text-4xl text-black uppercase tracking-[0.06em] mb-8">
        {page.title}
      </h1>
      <div className="space-y-5">
        {paragraphs.map((p, i) => (
          <p key={i} className="text-sm lg:text-[15px] text-[#444] leading-relaxed whitespace-pre-wrap">
            {p}
          </p>
        ))}
      </div>
      {page.images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-10">
          {page.images.map((img, i) => (
            <img key={i} src={img} alt="" loading="lazy" className="w-full aspect-[3/4] object-cover" />
          ))}
        </div>
      )}
      {page.ctaLabel && page.ctaLink && (
        <div className="mt-10">
          <Link
            to={localePath(page.ctaLink, locale, marketPrefix)}
            className="inline-block bg-black text-white text-[11px] uppercase tracking-[0.14em] px-8 py-3.5 hover:bg-[#333] transition-colors"
          >
            {page.ctaLabel}
          </Link>
        </div>
      )}
    </main>
  );
}
